import { type SecurityMode } from '@/hooks/useSecurityPolicy';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Props {
  /** Current value. `null` = inherit. */
  value: SecurityMode | null;
  /** Set to false to omit the "inherit" option (org-wide page can't inherit). */
  allowInherit?: boolean;
  onChange: (mode: SecurityMode | null) => void;
  disabled?: boolean;
}

/**
 * Port-security mode picker.
 * Used by Org + Region security policy sections.
 *
 * Internally maps null ↔ 'inherit' for the shadcn Select since the
 * shadcn primitive doesn't accept empty-string values.
 */
const INHERIT_SENTINEL = 'inherit';

export default function SecurityModeSelector({
  value,
  onChange,
  allowInherit = true,
  disabled,
}: Props) {
  return (
    <Select
      value={value ?? INHERIT_SENTINEL}
      disabled={disabled}
      onValueChange={(v) => {
        onChange(v === INHERIT_SENTINEL ? null : (v as SecurityMode));
      }}
    >
      <SelectTrigger className="w-full md:w-[28rem]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {allowInherit ? <SelectItem value={INHERIT_SENTINEL}>(inherit)</SelectItem> : null}
        <SelectItem value="open">Open — accept any device</SelectItem>
        <SelectItem value="managed">Managed — alert on unusual MAC count</SelectItem>
        <SelectItem value="strict">Strict — only allowlisted MACs</SelectItem>
      </SelectContent>
    </Select>
  );
}
